import { GenericPatient } from '@/models/GenericPatient.ts';
import { Appointment } from '@/models/appointment/Appointment.ts';
import { AppointmentBookingFormValues } from '@/models/appointment/AppointmentBookingFormValues.ts';
import { CommunicationContext } from '@/models/communication-context.ts';
import { InsuranceType } from '@/models/insurance.ts';

import { getCalendarId, getInstanceId, getPvsPatientId } from '@/utils/appointmentUtils.ts';
import { dayjs } from '@/utils/dayjsSetup.ts';
import { getInsuranceTypeFromAppointment, insuranceToInsuranceType } from '@/utils/insurance-utils.ts';
import { MISSING_PATIENT_ID } from '@/utils/patientUtils.ts';

type CommunicationFormValues = Pick<AppointmentBookingFormValues, 'calendar_pvs_id' | 'insurance' | 'date_time'>;

const isUsablePatientId = (patientId: string | undefined): patientId is string =>
    !!patientId && patientId !== MISSING_PATIENT_ID;

const isInsuranceType = (value: string): value is InsuranceType =>
    Object.values(InsuranceType).includes(value as InsuranceType);

// The endpoint takes a UTC ISO datetime with seconds, so normalise rather than forward whatever the source holds.
const toIsoDateTime = (value: string | undefined): string | undefined => {
    const parsed = dayjs(value);
    return value && parsed.isValid() ? parsed.toISOString() : undefined;
};

interface BuildBookingCommunicationContextParams {
    formData: CommunicationFormValues | undefined;
    currentPatient: GenericPatient | undefined;
    patientId: string | undefined;
    instanceId: string;
}

/** Scoped to the one booked appointment the entry point sits on, so its calendar and patient are the ones we send. */
export const buildBookingCommunicationContext = ({
    formData,
    currentPatient,
    patientId,
    instanceId,
}: BuildBookingCommunicationContextParams): CommunicationContext | undefined => {
    const date = toIsoDateTime(formData?.date_time);

    if (
        !formData?.calendar_pvs_id ||
        !formData.insurance ||
        !isUsablePatientId(patientId) ||
        !instanceId ||
        !currentPatient ||
        !date
    ) {
        return undefined;
    }

    return {
        instanceId: instanceId,
        patientId: patientId,
        patientName: `${currentPatient.personal.last_name} ${currentPatient.personal.first_name}`,
        dcCalendarId: formData.calendar_pvs_id,
        insuranceType: insuranceToInsuranceType(formData.insurance),
        date: date,
    };
};

export const buildAppointmentCommunicationContext = (
    appointment: Appointment,
    patientName: string
): CommunicationContext | undefined => {
    const insuranceKey = getInsuranceTypeFromAppointment(appointment);
    const instanceId = getInstanceId(appointment);
    const patientId = getPvsPatientId(appointment);
    const dcCalendarId = getCalendarId(appointment);
    const date = toIsoDateTime(appointment.start);

    if (!instanceId || !isUsablePatientId(patientId) || !dcCalendarId || !isInsuranceType(insuranceKey) || !date)
        return undefined;

    return {
        instanceId: instanceId,
        patientId: patientId,
        patientName: patientName,
        dcCalendarId: dcCalendarId,
        insuranceType: insuranceKey,
        date: date,
    };
};
